import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, BarChart3, CheckCircle2, ChevronRight, Film, Loader2, RefreshCw, Users } from 'lucide-react';
import { listSocialConnections } from './lib/socialAnalyticsApi';

const platforms = [
  { id: 'instagram', label: 'Instagram', contentLabel: 'reels and posts', accent: 'from-pink-500/20 to-purple-500/10', text: 'text-pink-300' },
  { id: 'x', label: 'X', contentLabel: 'posts', accent: 'from-sky-500/20 to-blue-500/10', text: 'text-sky-300' },
  { id: 'reddit', label: 'Reddit', contentLabel: 'posts', accent: 'from-orange-500/20 to-red-500/10', text: 'text-orange-300' },
  { id: 'tiktok', label: 'TikTok', contentLabel: 'videos', accent: 'from-cyan-500/20 to-pink-500/10', text: 'text-cyan-300' },
  { id: 'youtube', label: 'YouTube', contentLabel: 'videos and Shorts', accent: 'from-red-500/20 to-pink-500/10', text: 'text-red-300' }
];

const number = (value) => Number(value) || 0;
const formatNumber = (value) => number(value).toLocaleString('en-GB');
const formatDate = (value) => value
  ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : 'Not synced yet';

function SocialAnalyticsDrilldown({ session, dashboardId = 'vasia', onOpenConnections }) {
  const [connections, setConnections] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState('instagram');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const accessToken = session?.access_token;

  const load = async () => {
    if (!accessToken) return;
    setLoading(true);
    setError('');
    try {
      const payload = await listSocialConnections({ accessToken, dashboardId });
      setConnections(payload.connections || []);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [accessToken, dashboardId]);

  const selectedDefinition = platforms.find((item) => item.id === selectedPlatform) || platforms[0];
  const selectedConnections = useMemo(
    () => connections.filter((connection) => connection.platform === selectedPlatform),
    [connections, selectedPlatform]
  );
  const bestContent = useMemo(() => selectedConnections
    .flatMap((connection) => (connection.recentContent || []).map((content) => ({
      ...content,
      accountHandle: connection.accountHandle || connection.accountId,
      connectionId: connection.connectionId
    })))
    .sort((a, b) => number(b.performanceScore) - number(a.performanceScore))
    .slice(0, 10), [selectedConnections]);

  return (
    <section className="mb-8 overflow-hidden rounded-xl border border-purple-500/20 bg-gray-900">
      <div className="border-b border-gray-800 bg-gradient-to-r from-purple-500/10 via-transparent to-pink-500/10 p-5 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-purple-300">Account intelligence</p>
            <h2 className="mt-1 text-xl font-semibold text-white">Explore performance by platform and account</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-400">Choose a platform to open its connected accounts and see the strongest recent content captured during API sync.</p>
          </div>
          <button onClick={load} disabled={loading || !accessToken} className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-950/50 px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-50">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Refresh accounts
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2 md:grid-cols-5">
          {platforms.map((platform) => {
            const count = connections.filter((connection) => connection.platform === platform.id).length;
            const selected = selectedPlatform === platform.id;
            return (
              <button key={platform.id} onClick={() => setSelectedPlatform(platform.id)} className={`group rounded-xl border p-3 text-left transition ${selected ? `border-purple-400/50 bg-gradient-to-br ${platform.accent} shadow-lg shadow-purple-950/20` : 'border-gray-800 bg-gray-950/40 hover:border-gray-700 hover:bg-gray-800/70'}`}>
                <span className={`block text-sm font-semibold ${selected ? platform.text : 'text-gray-200'}`}>{platform.label}</span>
                <span className="mt-1 flex items-center justify-between text-xs text-gray-500"><span>{count} account{count === 1 ? '' : 's'}</span><ChevronRight size={14} className={selected ? 'text-purple-300' : 'text-gray-600'} /></span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-5 md:p-6">
        {error && <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</div>}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className={`text-sm font-semibold ${selectedDefinition.text}`}>{selectedDefinition.label} deep dive</p>
            <h3 className="mt-1 text-lg font-semibold text-white">{selectedConnections.length} connected account{selectedConnections.length === 1 ? '' : 's'}</h3>
          </div>
          <button onClick={onOpenConnections} className="inline-flex items-center gap-2 rounded-lg border border-purple-500/30 px-3 py-2 text-sm text-purple-200 hover:bg-purple-500/10">
            Manage API connections <ChevronRight size={15} />
          </button>
        </div>

        {selectedConnections.length ? (
          <>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {selectedConnections.map((connection) => {
                const content = connection.recentContent || [];
                const totalPerformance = content.reduce((sum, item) => sum + number(item.performanceScore), 0);
                return (
                  <div key={connection.connectionId} className="rounded-xl border border-gray-800 bg-gray-950/50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-white">{connection.accountHandle || connection.accountId}</p>
                        <p className="mt-1 truncate text-xs text-gray-500">Account ID: {connection.accountId}</p>
                      </div>
                      <CheckCircle2 size={17} className={connection.status === 'connected' ? 'text-green-300' : 'text-yellow-300'} />
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <div className="rounded-lg bg-gray-900 p-3"><p className="text-[11px] uppercase tracking-wider text-gray-500">Recent content</p><p className="mt-1 text-lg font-semibold text-white">{content.length}</p></div>
                      <div className="rounded-lg bg-gray-900 p-3"><p className="text-[11px] uppercase tracking-wider text-gray-500">Performance</p><p className="mt-1 text-lg font-semibold text-white">{formatNumber(totalPerformance)}</p></div>
                    </div>
                    <p className="mt-3 text-xs text-gray-500">Last sync: {formatDate(connection.lastSyncedAt)}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-6">
              <div className="flex items-center gap-2"><Film size={18} className={selectedDefinition.text} /><h3 className="font-semibold text-white">Best-performing {selectedDefinition.contentLabel}</h3></div>
              {bestContent.length ? (
                <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {bestContent.map((content, index) => (
                    <article key={`${content.connectionId}:${content.id || index}`} className="flex gap-3 rounded-xl border border-gray-800 bg-gray-950/50 p-4">
                      <div className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gradient-to-br ${selectedDefinition.accent} text-sm font-bold ${selectedDefinition.text}`}>#{index + 1}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0"><p className="line-clamp-2 text-sm font-semibold text-gray-100">{content.title || `${selectedDefinition.label} content`}</p><p className="mt-1 text-xs text-gray-500">{content.accountHandle} · {formatDate(content.createdAt)}</p></div>
                          {content.url && <a href={content.url} target="_blank" rel="noreferrer" aria-label="Open content" className="flex-none text-gray-500 hover:text-white"><ArrowUpRight size={17} /></a>}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-400">
                          {number(content.views) > 0 && <span>{formatNumber(content.views)} views</span>}
                          {number(content.reach) > 0 && <span>{formatNumber(content.reach)} reach</span>}
                          <span>{formatNumber(content.likes)} likes</span>
                          <span>{formatNumber(content.comments)} comments</span>
                          {number(content.shares) > 0 && <span>{formatNumber(content.shares)} shares</span>}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="mt-3 rounded-xl border border-dashed border-gray-700 bg-gray-950/30 p-6 text-center">
                  <BarChart3 className="mx-auto text-gray-600" size={24} />
                  <p className="mt-2 text-sm font-medium text-gray-300">Sync these accounts to rank their recent {selectedDefinition.contentLabel}</p>
                  <p className="mt-1 text-xs text-gray-500">The next successful sync will save a safe performance snapshot here. API keys remain encrypted and hidden.</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="mt-4 rounded-xl border border-dashed border-gray-700 bg-gray-950/30 p-8 text-center">
            <Users className="mx-auto text-gray-600" size={26} />
            <p className="mt-3 font-medium text-gray-200">No {selectedDefinition.label} accounts connected yet</p>
            <p className="mt-1 text-sm text-gray-500">Open Data Connections to add one or more accounts.</p>
          </div>
        )}
      </div>
    </section>
  );
}

export default SocialAnalyticsDrilldown;
